import type { ContentKind, Direction } from "../api";

// WHAT CAN BE IN THE MAILBOX — one entry per kind of file this service can meet,
// written in the order a delivery moves: what we send, what comes back, and the
// files that are neither.
//
// Keyed by `ContentKind`, which is the inspector contract's own enum: adding a
// content kind to the spec breaks this file until it is documented here. That is
// deliberate — an undocumented kind is exactly the one an operator will meet at
// two in the morning.

export interface CodeMeaning {
  code: string;
  means: string;
}

export interface FileKindDoc {
  /** The badge the file list shows for this kind. */
  badge: string;
  title: string;
  /** Which side of the exchange it appears on. */
  direction: Direction;
  deliveryType?: string;
  /** What the file IS. */
  what: string;
  /** What this service DOES with it — the part no guideline can tell you. */
  handling: string;
  /** The event it produces, if any. */
  event?: string;
  /** The transaction codes worth knowing when reading one. */
  codes?: readonly CodeMeaning[];
  /** The guideline that specifies it (a slug from GET /v1/guidelines). */
  guideline?: string;
  /** The API operations that cause this file to exist, if we write it. */
  operations?: readonly string[];
}

export const FILE_KINDS: Record<ContentKind, FileKindDoc> = {
  collections: {
    badge: "0601",
    title: "BS 0601 — collections",
    direction: "outbound",
    deliveryType: "0601",
    what: "The charges we raise against payer mandates: one section per creditor, and inside it a record per collection with its due date, amount and the text the payer will see on their payment overview.",
    handling:
      "Built by @betalingsservice/format when a caller posts a collection batch, written to the outbound bucket under the caller's deliveryReference using a conditional create, and shipped to the bank by the S3→SFTP Lambda. The conditional create prevents duplicate file creation; transport retries are a separate step.",
    codes: [
      { code: "§0112", means: "Automatic debit — the payer's account is charged." },
      { code: "§0117", means: "Payment slip — the payer is sent a slip to pay." },
      { code: "0280", means: "Collection, automatic debit." },
      { code: "0285", means: "Collection, payment slip." },
      { code: "0240", means: "Debtor information." },
      { code: "0241", means: "Text to debtor." },
    ],
    guideline: "bs-data-suppliers-guidelines",
    operations: ["submitCollectionBatch"],
  },

  mandate_changes: {
    badge: "0605",
    title: "BS 0605 — mandate changes",
    direction: "outbound",
    deliveryType: "0605",
    what: "Everything we ask the bank to change: stop a collection already submitted, register a mandate, renumber a customer, cancel a mandate. One delivery type, four sections.",
    handling:
      "Built and shipped like a 0601, from four endpoints. Validation receipts confirm acceptance. Mandate changes are reported in BS 0603; stop results arrive in BS 0602 after advice or BS 0621 before advice. BS 0621 is currently unsupported here.",
    codes: [
      { code: "§0105", means: "Stop collections already submitted." },
      { code: "§0120", means: "Register a mandate (from bank details, or by copying one)." },
      { code: "§0125", means: "Change the customer number on a mandate." },
      { code: "§0126", means: "Cancel a mandate." },
    ],
    guideline: "bs-data-suppliers-guidelines",
    operations: [
      "submitCollectionStops",
      "submitMandateRegistrations",
      "submitCustomerNumberChanges",
      "submitMandateCancellations",
    ],
  },

  payment_advice: {
    badge: "0602",
    title: "BS 0602 — payment advice",
    direction: "inbound",
    deliveryType: "0602",
    what: "What became of the collections: settled, rejected, cancelled or charged back — plus, on the section totals, what the bank actually deposited for each creditor.",
    handling:
      "Mapped whole into ONE payment.advice event: one entry in sections[] per creditor, each with that creditor's deposit and its outcomes in collections[]. No per-record splitting — the consumer iterates. A delivery that is malformed, incomplete or does not add up to its own section and delivery totals is refused whole (reason: invalid_inbound_delivery), as is one carrying section 0216, whose layout is not yet confirmed.",
    event: "payment.advice",
    codes: [
      { code: "0236", means: "Payment settled." },
      { code: "0297", means: "Payment slip settled." },
      { code: "0237", means: "Payment rejected." },
      { code: "0238", means: "Payment cancelled." },
      { code: "0239", means: "Payment charged back." },
      { code: "0299", means: "Payment slip charged back." },
    ],
    guideline: "bs-data-suppliers-guidelines",
  },

  mandate_information: {
    badge: "0603",
    title: "BS 0603 — mandate information",
    direction: "inbound",
    deliveryType: "0603",
    what: "Mandate changes the bank reports — the payer signed up, the mandate went active, somebody cancelled it — whether or not we asked for them.",
    handling:
      "Mapped whole into ONE mandate.updated event, with every change in updates[] carrying its own creditor. A delivery that is malformed, incomplete or does not add up to its own counts is refused whole (reason: invalid_inbound_delivery).",
    event: "mandate.updated",
    codes: [
      { code: "0230", means: "Mandate active." },
      { code: "0231", means: "Mandate registered." },
      { code: "0232", means: "Mandate cancelled by the bank." },
      { code: "0233", means: "Mandate cancelled by the creditor." },
      { code: "0234", means: "Mandate cancelled by Betalingsservice." },
    ],
    guideline: "bs-data-suppliers-guidelines",
  },

  validation_receipt: {
    badge: "rcpt",
    title: "PBCNET41 — validation receipt",
    direction: "inbound",
    what: "The bank's verdict on a delivery we sent: accepted, or rejected with validation errors.",
    handling:
      "Becomes a batch.accepted or batch.rejected event. Its deliveryReference is the bank-assigned delivery number; submission correlation needs a separate mapping.",
    event: "batch.accepted / batch.rejected",
    guideline: "bs-data-suppliers-guidelines",
  },

  transmission_receipt: {
    badge: "—",
    title: "PBCNET40 — transmission receipt",
    direction: "inbound",
    what: "Confirmation that a file arrived on the bank's server at all — the layer below validation.",
    handling: "Recognised and deliberately ignored: it says nothing about the delivery's content. No event.",
    guideline: "bs-data-communication-rules-da",
  },

  human_readable_report: {
    badge: "—",
    title: "Cover sheets and reports",
    direction: "inbound",
    what: "The F-file that accompanies each D-file, and the readable reports the bank generates — the same information as the delivery, set as text for a person.",
    handling: "Recognised and ignored: acting on both the data file and its cover sheet would double-count. No event.",
  },

  sftp_admin: {
    badge: "—",
    title: "SFTP administration files",
    direction: "inbound",
    what: "Artefacts of the transport itself, put in the mailbox by the SFTP service rather than by Betalingsservice.",
    handling: "Recognised and ignored. No event.",
    guideline: "sftp-guide-en",
  },

  unrecognised: {
    badge: "?",
    title: "Anything else",
    direction: "inbound",
    what: "A file that matches none of the above — a delivery type this service does not implement (BS 0621, BS 0686), or something new.",
    handling:
      "The processor THROWS rather than dropping it, so the Lambda fails and Sentry reports it with reason: unhandled_inbound_file. The file stays in the bucket and can be read here — which is what this inspector is for.",
  },
};

/** The catalogue in the order it is presented: what we send, then what arrives. */
export const FILE_KIND_ORDER: readonly ContentKind[] = [
  "collections",
  "mandate_changes",
  "payment_advice",
  "mandate_information",
  "validation_receipt",
  "transmission_receipt",
  "human_readable_report",
  "sftp_admin",
  "unrecognised",
];

/** The file-name convention, which is data in its own right. */
export const NAME_PREFIXES: ReadonlyArray<{ letter: string; danish: string; means: string }> = [
  { letter: "D", danish: "Datafil", means: "The machine-readable delivery. This is what becomes an event." },
  { letter: "F", danish: "Følgeseddel", means: "The cover sheet generated beside a D file — the same data as readable text." },
  { letter: "V", danish: "Valideringskvittering", means: "Whether a delivery we sent was accepted (the PBCNET41)." },
  { letter: "T", danish: "Transmissionskvittering", means: "Whether it arrived on the server at all (the PBCNET40)." },
  { letter: "Z", danish: "—", means: "A D file, zipped. Only for mailboxes configured for zipped format." },
];

/** The record types every fixed-width delivery is built from. */
export const RECORD_TYPES: ReadonlyArray<{ type: string; means: string }> = [
  { type: "002", means: "Data delivery start — delivery type, delivery number, data supplier. The file's identity." },
  { type: "012", means: "Section start — which creditor, and which section type (§0112, §0211, …) the records below belong to." },
  { type: "022", means: "Optional functionality for the debtor (outbound only)." },
  { type: "042", means: "The record that carries the money or the change: due date, amount, transaction code." },
  { type: "052", means: "Text to the debtor — what the payer reads on their payment overview." },
  { type: "062", means: "Separate text on a payment slip." },
  { type: "092", means: "Section end — the section's own record count and total." },
  { type: "992", means: "Data delivery end — the totals the whole file declares, which the inspector checks the records against." },
];
